/**
 * Main App component with routing
 */
import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import Layout from '@/components/Layout';
import Login from '@/pages/legacy-admin/Login';
// Command Centre + builders + analytics — these components were
// fully implemented under src/pages/ but their routes were dropped from
// App.tsx at some point (merge/refactor regression). Restored 2026-04-24.
//
// The Facilitator Portal split out 2026-04-28 — it's now a separate
// SPA at /facilitator/ via Vite multi-input. The legacy
// /facilitator-portal/* path redirects to the new root (see
// FacilitatorPortalRedirect below).
import CommandCentre from '@/pages/command-centre/CommandCentre';
import MicroClassBuilder from '@/pages/micro-class-builder/MicroClassBuilder';
import SkillDrillBuilder from '@/pages/skill-drill-builder/SkillDrillBuilder';
import ScenarioBuilder from '@/pages/scenario-builder/ScenarioBuilder';
import ToDo from '@/pages/class-creator/ToDo';
import { TOOL, otherToolUrl } from '@/lib/tool';
import { toolOfPath } from '@/lib/nav';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

/**
 * Hard-redirect for legacy `/facilitator-portal/*` bookmarks.
 *
 * `<Navigate>` would do a SPA-internal redirect, but the FP now lives
 * on a different static HTML entry (facilitator/index.html). We need a
 * full page navigation so the browser actually loads the FP bundle.
 */
function FacilitatorPortalRedirect() {
  useEffect(() => {
    window.location.replace('/facilitator/');
  }, []);
  return (
    <div className="flex items-center justify-center h-screen">
      <p className="text-muted-foreground">
        Redirecting to the Facilitator Portal…
      </p>
    </div>
  );
}

/**
 * The split (Design's sketch, Nino's go 27 Oct): a page that belongs to the other tool opens there,
 * so old links and bookmarks keep working. A full page load, since the tool is fixed per page.
 */
function ToolForward({ children }: { children: React.ReactNode }) {
  const { pathname, search } = useLocation();
  const target = pathname === '/' ? TOOL : toolOfPath(pathname);
  useEffect(() => {
    if (target !== TOOL) window.location.replace(otherToolUrl(target, pathname + search));
  }, [target, pathname, search]);
  if (target !== TOOL) return <p className="p-8 text-muted-foreground">Opening it in the right tool…</p>;
  return <>{children}</>;
}

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AppContent() {
  const { checkAuth } = useAuth();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* 2026-10-06 (Nino's audit): the password reset pages are removed.
          The server never had the route, and sign-in is moving to email
          codes (Scoring, 5 Oct). */}
      {/* Legacy FP path — kicks the user out of the admin SPA into
          the standalone facilitator entry. Sits OUTSIDE ProtectedRoute
          so a logged-out facilitator with an old bookmark still gets
          handed off to the FP's own auth flow. */}
      <Route path="/facilitator-portal" element={<FacilitatorPortalRedirect />} />
      <Route path="/facilitator-portal/*" element={<FacilitatorPortalRedirect />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <ToolForward>
            <Layout>
              <Routes>
                {/* Default landing: Today in the Command Centre, To do in the Class Creator. */}
                <Route path="/" element={<Navigate to={TOOL === 'class-creator' ? '/to-do' : '/command-centre'} replace />} />
                <Route path="/to-do" element={<ToDo />} />

                {/* 2026-10-06 (Nino's Command Centre audit): the legacy workshop
                    list, results, processing and status pages, Upload (admin)
                    and Cache are removed with their files: their server routes
                    are gone or nothing used them (Scoring, 5 Oct). */}
                {/* /config /dlq /costs /system placeholder routes removed
                    2026-05-27 along with their dead source files. The CC
                    surfaces (Operations / Content / Configuration tabs)
                    replace them. */}

                {/* Restored routes — Command Centre and siblings.
                    CommandCentre has its own internal <Routes> so it needs
                    a splat path; the rest are single-page but using splat
                    is harmless and future-proofs nested routing.

                    NOTE: FacilitatorPortal route removed 2026-04-28 — it's
                    now a separate SPA at /facilitator/. The legacy
                    /facilitator-portal/* paths redirect via
                    FacilitatorPortalRedirect at the top of <Routes>. */}
                <Route path="/command-centre/*" element={<CommandCentre />} />
                <Route path="/micro-class-builder/*" element={<MicroClassBuilder />} />
                <Route path="/workshop-builder/*" element={<SkillDrillBuilder />} />
                <Route path="/scenario-builder/*" element={<ScenarioBuilder />} />
                {/* The two Analytics pages merged into Content gaps (Nino approved Design's menu, 12 Oct). */}
                <Route path="/analytics/*" element={<Navigate to="/command-centre/content-gaps" replace />} />
                <Route path="/workshop-analytics/*" element={<Navigate to="/command-centre/content-gaps?for=drills" replace />} />

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Layout>
            </ToolForward>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
